/**
 * The business calendar: which day it is for the organisation.
 *
 * A form's "today" default — a hire date, an effective date, an enrolment —
 * is the organisation's calendar day, not UTC's and not this computer's.
 * `new Date().toISOString().slice(0, 10)` is UTC, which in Manila is still
 * yesterday until 08:00, so everything done in the first hours of the working
 * day was defaulted to the day before.
 *
 * The zone is the organisation's (Settings → Company), delivered with the
 * session policy after sign-in; until then, and for every current tenant, it
 * is Asia/Manila.
 */

export const DEFAULT_TIME_ZONE = 'Asia/Manila';

let businessTimeZone = DEFAULT_TIME_ZONE;
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    try {
      formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      formatter = formatterFor(DEFAULT_TIME_ZONE);
    }
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

/** Use the organisation's zone from now on. Unknown or empty zones are ignored. */
export function setBusinessTimeZone(timeZone: string | null | undefined): void {
  if (!timeZone) return;
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone });
    businessTimeZone = timeZone;
  } catch {
    // Keep the zone we have: a typo in settings must not shift every date.
  }
}

export function getBusinessTimeZone(): string {
  return businessTimeZone;
}

/** `YYYY-MM-DD` of an instant on the business calendar. */
export function businessDateOf(
  instant: string | Date,
  timeZone: string = businessTimeZone,
): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  if (Number.isNaN(date.getTime())) return '';
  const parts = formatterFor(timeZone).formatToParts(date);
  const part = (type: string) => parts.find((entry) => entry.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** Today on the business calendar, `YYYY-MM-DD`. */
export function todayInBusiness(
  now: Date = new Date(),
  timeZone: string = businessTimeZone,
): string {
  return businessDateOf(now, timeZone);
}

/** `YYYY-MM-DD` shifted by whole days — calendar arithmetic, no timezone involved. */
export function addDaysToKey(key: string, days: number): string {
  const date = new Date(`${key}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
